import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmModal from '../components/common/ConfirmModal';
import { Input } from '../components/ui/input';

/**
 * Confirmação de ação destrutiva com a cara do produto.
 *
 * Existia um `window.confirm()`/`prompt()` nativo em 8 pontos do sistema (excluir
 * pendência, risco, mudança, custo, anexo, membro e organização, mais a exclusão
 * em lote da tabela): diálogo do navegador, fora do tema, sem token de perigo.
 * O `ConfirmModal` resolvia isso, mas exigia estado em cada tela, e foi por isso
 * que ninguém adotou. Aqui ele vira uma chamada assíncrona de uma linha:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ message: t.confirmDelete, variant: 'danger' }))) return;
 */
export interface ConfirmOptions {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  /** Exige digitar exatamente este texto para liberar o botão (exclusão de alto impacto). */
  requireText?: string;
  requireTextLabel?: string;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export const useConfirm = (): ConfirmFn => {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm precisa estar dentro de ConfirmProvider');
  return confirm;
};

export const ConfirmProvider = ({ children }: { children: React.ReactNode }) => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [typed, setTyped] = useState('');
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setTyped('');
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = (answer: boolean) => {
    resolverRef.current?.(answer);
    resolverRef.current = null;
    setOptions(null);
    setTyped('');
  };

  const needsText = Boolean(options?.requireText);
  const textMatches = !needsText || typed.trim() === options?.requireText;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <ConfirmModal
          isOpen
          title={options.title}
          message={options.message}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant ?? 'danger'}
          confirmDisabled={!textMatches}
          extra={needsText ? (
            <div className="flex flex-col gap-1.5 pt-1">
              {options.requireTextLabel && (
                <span className="text-[11px] font-semibold uppercase tracking-[0.09em] text-[var(--color-text-muted)]">
                  {options.requireTextLabel}
                </span>
              )}
              <Input
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={options.requireText}
                className="h-8 border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[13px]"
              />
            </div>
          ) : undefined}
          onConfirm={() => { if (textMatches) close(true); }}
          onCancel={() => close(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
};
